// IMPORTANT: THis file is used on frontend and backend, so do not include
// things here that do not need to be part of the frontend. Instead, use
// the front-end/back-end specific utils files.
import _ from 'lodash';
import { slugify, wowGameDataSlugify } from '../../../shared/common/slugify';
import { TCharacterRole } from '../../../shared/common/types/common';
import { TRealmResource } from './types/utilityTypes';
import { Expansion } from '../../../shared/gameConfig/expansion';

export function safeCharacterName(str: string) {
  return safeCapitalize(str.split('-', 2)[0]);
}

// Like an assert but throws an exception if the condition does not pass
// This is NOT compiled out, so you can have side effects in the condition.
export function ensure<T>(condition: T, exceptionMessage: string, errorClass: any = Error) {
  if (!condition) {
    throw new errorClass(exceptionMessage);
  }
  return condition!;
}

// Used in filter to remove undefined/null values
// Used like .filter(filterNotEmpty)
export function filterNotEmpty<TValue>(value: TValue | null | undefined): value is TValue {
  return value !== null && value !== undefined;
}

export type TSafeSetIntervalReturn = {
  timeout: NodeJS.Timeout | null;
  clear: () => void;
};

export function safeSetInterval(callback: any, ms: number) {
  const returnValue: TSafeSetIntervalReturn = {
    timeout: null,
    clear: () => {
      if (returnValue.timeout) {
        clearTimeout(returnValue.timeout);
        returnValue.timeout = null;
      }
    }
  };

  async function timeoutFunction() {
    try {
      await callback();
    } finally {
      returnValue.timeout = setTimeout(() => timeoutFunction(), ms);
    }
  }

  timeoutFunction();

  return returnValue;
}

// capitalizes the word in a safe manner, because a regular _.capitalize() will
// cause letters like 'ß' to be converted to 'SS'
export function safeCapitalize(str: string) {
  const firstCharacter = str.charAt(0);

  let capitalizedFirstCharacter = firstCharacter.toUpperCase();
  if (capitalizedFirstCharacter.length > 1) {
    capitalizedFirstCharacter = firstCharacter;
  }

  return capitalizedFirstCharacter + str.substring(1).toLowerCase();
}

const CHARACTER_REGION_ARMORY_INFO: any = {
  us: {
    domain: 'worldofwarcraft.com',
    locale: 'en-us'
  },
  eu: {
    domain: 'worldofwarcraft.com',
    locale: 'en-gb'
  },
  kr: {
    domain: 'worldofwarcraft.com',
    locale: 'ko-kr'
  },
  tw: {
    domain: 'worldofwarcraft.com',
    locale: 'zh-tw'
  },
  cn: {
    domain: 'www.wowchina.com',
    locale: 'zh-cn'
  }
};

const GUILD_REGION_ARMORY_INFO: any = {
  us: {
    domain: 'worldofwarcraft.com',
    locale: 'en-us'
  },
  eu: {
    domain: 'worldofwarcraft.com',
    locale: 'en-gb'
  },
  kr: {
    domain: 'worldofwarcraft.com',
    locale: 'ko-kr'
  },
  tw: {
    domain: 'worldofwarcraft.com',
    locale: 'zh-tw'
  },
  cn: {
    domain: 'www.wowchina.com',
    locale: 'zh-cn'
  }
};

// live on worldofwarcraft.com site now
export function buildCharacterArmoryUrl(regionSlug: string, realmSlug: string, characterName: string) {
  const info = CHARACTER_REGION_ARMORY_INFO[regionSlug] || CHARACTER_REGION_ARMORY_INFO.us;
  return `https://${info.domain}/${info.locale}/character/${regionSlug}/${encodeURIComponent(
    wowGameDataSlugify(realmSlug)
  )}/${encodeURIComponent(characterName)}`;
}

// these still use the battlenet domains
export function buildGuildArmoryUrl(regionSlug: string, realmSlug: string, guildName: string) {
  const info = GUILD_REGION_ARMORY_INFO[regionSlug] || GUILD_REGION_ARMORY_INFO.us;
  let cleanedGuildName = guildName;
  if (regionSlug === 'cn') {
    cleanedGuildName = guildName.replace(/\s+\(.*?\)$/, '');
  }
  return `https://${info.domain}/${info.locale}/guild/${regionSlug}/${encodeURIComponent(
    wowGameDataSlugify(realmSlug)
  )}/${encodeURIComponent(slugify(cleanedGuildName))}`;
}

// TODO: link to wowhead instead
export function buildArmoryTalentCalculatorUrl(cls: any, spec: any, talentString: string) {
  const wowCompatibleTalentString = (talentString || '')
    .split('')
    .map((v) => safeParseInt(v, 0) + 1)
    .join('');
  return `https://worldofwarcraft.com/en-us/game/talent-calculator#${cls.slug}/${spec.slug}/talents=${wowCompatibleTalentString}`;
}

export function buildWowheadProfileUrl(name: string) {
  return `https://www.wowhead.com/user=${encodeURIComponent(name)}`;
}

export function buildWarcraftLogsCharacterProfileUrl(regionSlug: string, realmInfo: TRealmResource, name: string) {
  let realmSlug = realmInfo.slug;
  if (['ru_RU', 'zh_TW', 'zh_CN', 'ko_KR'].indexOf(realmInfo.locale) !== -1) {
    realmSlug = realmInfo.altSlug;
    // from this support request:
    // If the player is from one of the four Russian realms that have
    // character "й" in their names (Ревущий Фьорд, Черный Шрам, Разувий, Борейская Тундра),
    // the generated Warcraftlogs link will be invalid. For it to be valid, character "й"
    // has to be replaced by "и".
    realmSlug = realmSlug.replace(/й/g, 'и');
  }
  return `https://classic.warcraftlogs.com/character/${regionSlug}/${realmSlug}/${name}`;
}

export function buildRaidbotsCharacterProfileUrl(regionSlug: string, realmInfo: TRealmResource, name: string) {
  let realmSlug = realmInfo.slug;
  if (['zh_TW', 'zh_CN', 'ko_KR'].indexOf(realmInfo.locale) !== -1) {
    realmSlug = realmInfo.altSlug;
  }
  return `https://www.raidbots.com/simbot/quick?region=${regionSlug}&realm=${realmSlug}&name=${name}`;
}

export function buildWarcraftLogsGuildProfileUrl(regionSlug: string, realmInfo: TRealmResource, name: string) {
  let realmSlug = realmInfo.slug;
  if (['ru_RU', 'zh_TW', 'zh_CN', 'ko_KR'].indexOf(realmInfo.locale) !== -1) {
    realmSlug = realmInfo.altSlug;
    // from this support request:
    // If the player is from one of the four Russian realms that have
    // character "й" in their names (Ревущий Фьорд, Черный Шрам, Разувий, Борейская Тундра),
    // the generated Warcraftlogs link will be invalid. For it to be valid, character "й"
    // has to be replaced by "и".
    realmSlug = realmSlug.replace(/й/g, 'и');
  }
  return `https://classic.warcraftlogs.com/guild/${regionSlug}/${realmSlug}/${name}`;
}

export function buildTwitchProfileUrl(name: string) {
  if (!name) {
    return '';
  }
  return `https://twitch.tv/${encodeURIComponent(name)}`;
}

export function buildYouTubeProfileUrl(name: string) {
  if (!name) {
    return '';
  }

  // regex found here: https://webapps.stackexchange.com/a/101153
  if (name.match(/^UC[0-9A-Za-z_-]{21}[AQgw]$/)) {
    return `https://youtube.com/channel/${encodeURIComponent(name)}`;
    // youtube allows custom handles which will always start with an @.
  } else if (name.charAt(0) === '@') {
    return `https://youtube.com/${encodeURIComponent(name)}`;
  } else {
    return `https://youtube.com/user/${encodeURIComponent(name)}`;
  }
}

export function buildTwitterProfileUrl(name: string) {
  if (!name) {
    return '';
  }
  return `https://twitter.com/${encodeURIComponent(name)}`;
}

export function uniqueValues(list: string[]) {
  const dict: any = {};
  list.forEach((element: string) => (dict[element] = true));
  return Object.keys(dict);
}

export function safeParseJson(str: string) {
  try {
    const res = JSON.parse(str);
    return res;
  } catch (e) {
    return null;
  }
}

export function safeParseInt(str: string, defaultValue?: number) {
  const res = parseInt(str, 10);
  if (isNaN(res)) {
    return defaultValue || 0;
  }
  return res;
}

export function safeParseFloat(str: string, defaultValue?: number) {
  const res = parseFloat(str);
  if (isNaN(res)) {
    return defaultValue || 0;
  }
  return res;
}

export function safeParseIntOrUndefined(str: string, defaultValue?: number | undefined) {
  const res = parseInt(str, 10);
  if (isNaN(res)) {
    return defaultValue;
  }
  return res;
}

export function safeParseIntCSV(str: string, defaults: number[] = []) {
  if (str === undefined) {
    return defaults;
  }
  const results: number[] = [];
  for (const part of str.split(',')) {
    const value = parseInt(part, 10);
    if (value !== undefined && !isNaN(value)) {
      results.push(value);
    }
  }
  return results;
}

export function stripDashes(str: string) {
  return str.replace(/\-/, '');
}

// Blizzard changed the reset time on November 16th:
//
// Weekly Reset Time Changing to 05:00 CET on 16 November
//
// https://eu.forums.blizzard.com/en/wow/t/weekly-reset-time-changing-to-0500-cet-on-16-november/398498

// FIXME: send this up from server
type TClassData = {
  id: number;
  name: string;
  slug: string;
  color: string;
  minExpansion?: Expansion;
  faction?: 'horde' | 'alliance';
};

export const CLASS_DATA: TClassData[] = [
  {
    id: 1,
    name: 'Warrior',
    slug: 'warrior',
    color: '#c79c6e'
  },
  {
    id: 2,
    name: 'Paladin',
    slug: 'paladin',
    color: '#f58cba'
  },
  {
    id: 3,
    name: 'Hunter',
    slug: 'hunter',
    color: '#abd473'
  },
  {
    id: 4,
    name: 'Rogue',
    slug: 'rogue',
    color: '#fff569'
  },
  {
    id: 5,
    name: 'Priest',
    slug: 'priest',
    color: '#fff'
  },
  {
    id: 6,
    minExpansion: Expansion.WrathOfTheLichKing,
    name: 'Death Knight',
    slug: 'death-knight',
    color: '#c41f3b'
  },
  {
    id: 7,
    faction: 'horde',
    name: 'Shaman',
    slug: 'shaman',
    color: '#0070de'
  },
  {
    id: 8,
    name: 'Mage',
    slug: 'mage',
    color: '#69ccf0'
  },
  {
    id: 9,
    name: 'Warlock',
    slug: 'warlock',
    color: '#9482c9'
  },
  {
    id: 10,
    minExpansion: Expansion.MistsOfPandaria,
    name: 'Monk',
    slug: 'monk',
    color: '#00ff96'
  },
  {
    id: 11,
    name: 'Druid',
    slug: 'druid',
    color: '#ff7d0a'
  }
  // {
  //   id: 12,
  //   minExpansion: Expansion.Legion,
  //   name: 'Demon Hunter',
  //   slug: 'demon-hunter',
  //   color: '#a330c9'
  // },
  // {
  //   id: 13,
  //   minExpansion: Expansion.Dragonflight,
  //   name: 'Evoker',
  //   slug: 'evoker',
  //   color: '#33937f'
  // }
];

export const CLASSES_BY_ID = CLASS_DATA.reduce((accum: any, cls: any) => {
  accum[cls.id] = cls;
  return accum;
}, {});

export const CLASSES_BY_SLUG = CLASS_DATA.reduce((accum: any, cls: any) => {
  accum[cls.slug] = cls;
  return accum;
}, {});

const FACTION_CLASSES = CLASS_DATA.reduce(
  (accum: any, cls: any) => {
    if (typeof cls.faction === 'undefined') {
      accum.horde.push(cls);
      accum.alliance.push(cls);
    } else if (cls.faction === 'horde') {
      accum.horde.push(cls);
    } else if (cls.faction === 'alliance') {
      accum.alliance.push(cls);
    } else {
      // unexpected value
    }
    return accum;
  },
  { horde: [], alliance: [] }
);

export function getClassesByFaction(faction: 'horde' | 'alliance') {
  return FACTION_CLASSES[faction];
}

export function infoForClass(classId: number): any {
  return CLASSES_BY_ID[classId];
}

// FIXME: send this up from server
const ROLE_CLASS_IDS: any = {
  tank: [1, 2, 6, 10, 11, 12],
  healer: [2, 5, 7, 10, 11, 13]
};

export function canPerformRole(classId: number, role: TCharacterRole) {
  if (ROLE_CLASS_IDS[role]) {
    return ROLE_CLASS_IDS[role].indexOf(classId) !== -1;
  }
  return true;
}

export function delayForMs(ms: number): Promise<any> {
  return new Promise((resolve: any) => {
    setTimeout(() => resolve(), ms);
  });
}

/**
 * @param patch either x.x.x or x.x
 */
export function convertPatchStringToNumber(patch: string): number {
  const patchSemver = patch.split('.');

  const majorVersion = safeParseInt(patchSemver[0], 0);
  const minorVersion = safeParseInt(patchSemver[1], 0);
  const patchVersion = safeParseInt(patchSemver[2], 0);

  if (minorVersion >= 10 || patchVersion >= 10) {
    throw new Error(`Version minor or patch is over 10 and is not supported to be converted as number: ${patch}`);
  }

  let patchNumber = 0;

  patchNumber += majorVersion * 100;
  patchNumber += minorVersion * 10;
  patchNumber += patchVersion;

  return patchNumber;
}

/*
 * @param input
 * Remove whitespace, linebreak, as well as the ZWSP character
 */
export function safeTrim(input: string): string {
  if (!input) {
    return '';
  }

  if (input.match(/^[\s\u200B]+$/)) {
    return '';
  }

  return input.trim();
}

const GUILD_ID_INFO = [
  // Echo
  {
    id: 1047044,
    color: '#ff4e00',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/echo/logo.png'
  },
  // Liquid
  {
    id: 1712677,
    color: '#ffffff',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/liquid/guildlogo.png'
  },
  // // Echo - for XCC TR event
  // {
  //   id: 1739612,
  //   color: '#cd1d2d',
  //   logo: 'https://cdn-classic.raiderio.net/images/rwf/sepulcher-of-the-first-ones/echo/guildlogo.png'
  // },
  // // Liquid - for XCC TR event
  // {
  //   id: 1803240,
  //   color: '#ffffff',
  //   logo: 'https://cdn-classic.raiderio.net/images/rwf/sepulcher-of-the-first-ones/liquid/guildlogo.png'
  // },
  // Pieces
  {
    id: 1727102,
    color: '#4498cb',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/pieces/guildlogo.png'
  },
  // FatSharkYes',
  {
    id: 1213,
    color: '#0090d3',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/fatsharkyes/logo.png'
  },
  // Skyline
  {
    id: 1866222,
    color: '#c30d23',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/skyline/logo.png'
  },
  // JTH
  {
    id: 1873940,
    color: '#ff3332',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/jth/logo.png'
  },
  // Method
  {
    id: 316123,
    color: '#ff9015',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/method/logo.png'
  },
  // Exorsus
  {
    id: 21308,
    color: '#9e7530',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/exorsus/logo.png'
  },
  // BDG
  {
    id: 846835,
    color: '#a02c2f',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/bdg/logo.png'
  },
  // Aversion
  {
    id: 203618,
    color: '#9e9e9e',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/aversion/logo.png'
  },
  // Alpha
  {
    id: 899776,
    color: '#25799e',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/alpha/logo.png'
  },
  // Club Camel,
  {
    id: 6585,
    color: '#b9a25e',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/club-camel/logo.png'
  },
  // Imperative
  {
    id: 1115725,
    color: '#04abe4',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/imperative/logo.png'
  },
  // The Early Shift
  {
    id: 30591,
    color: '#b2202d',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/the-early-shift/logo.png'
  },
  // Aster - #1568876
  {
    id: 1568876,
    color: '#ffffff',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/aster/logo.png'
  },
  // Instant Dollars
  {
    id: 1916,
    color: '#32c654',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/instant-dollars/logo.png'
  },
  // Vesper
  {
    id: 1175663,
    color: '#ffffff',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/vesper/logo.png'
  },
  // Chaojie
  {
    id: 1836822,
    color: '#f45d0e',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/chaojie/logo.png'
  },
  // KeaHoarl
  {
    id: 560287,
    color: '#1c74ba',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/keahoarl/logo.png'
  },
  // Wings
  {
    id: 1175973,
    color: '#bbb3ae',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/wings/logo.png'
  },
  // Exposed
  {
    id: 138698,
    color: '#02739f',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/exposed/logo.png'
  },
  // Honestly
  {
    id: 2387,
    color: '#144282',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/honestly/logo.png'
  },
  // Honolulu
  {
    id: 42849,
    color: '#144282',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/honolulu/logo.png'
  }, // Northern Sky
  {
    id: 656001,
    color: '#04f9f9',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/northern-sky/logo.png'
  },
  // poptartcorndoG
  {
    id: 1133352,
    color: '#fa9ccc',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/poptartcorndog/logo.png'
  },
  // Stay Mad
  {
    id: 733010,
    color: '#7d4a2d',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/stay-mad/logo.png'
  },
  // spk
  {
    id: 5834,
    color: '#b7647a',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/spk/logo.png'
  },
  // xd
  {
    id: 649287,
    color: '#0e7a8b',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/xd/logo.png'
  },
  // LEVELS
  {
    id: 1649324,
    color: '#b47c13',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/levels/logo.png'
  },
  // Project
  {
    id: 567858,
    color: '#8a8b8a',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/project/logo.png'
  },
  // Ethical
  {
    id: 909517,
    color: '#c02b32',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/ethical/logo.png'
  },
  // Melee Mechanics
  {
    id: 558946,
    color: '#c48709',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/melee-mechanics/logo.png'
  },
  // Nerd Crew
  {
    id: 1100476,
    color: '#e0b559',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/nerd-crew/logo.png'
  },
  // Revolutionist
  {
    id: 1390863,
    color: '#fafac3',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/revolutionist/logo.png'
  },
  // Tony Halme Pro Skater
  {
    id: 1060875,
    color: '#ed262a',
    logo: 'https://cdn-classic.raiderio.net/images/rwf/aberrus-the-shadowed-crucible/tony-halme/logo.png'
  }
];

const guildMetadata = groupByDictUnique(GUILD_ID_INFO, 'id');

export function getGuildLogo(guildId: number): string | null {
  return guildMetadata[guildId]?.logo || null;
}

export function getGuildColor(guildId: number): string | null {
  return guildMetadata[guildId]?.color || null;
}

export function roundScore(score: number, precision: number = 1) {
  return parseFloat(score.toFixed(precision));
}

type TItemLink = {
  color: string;
  itemId: number;
  enchantId: number | null;
  gemIds: number[];
  bonusIds: number[];
};

export function parseItemLink(itemLink: string): TItemLink | null {
  const res: any = {};

  const linkParts = itemLink.split('|');

  res.color = linkParts[1].slice(1);

  const itemStringRaw = linkParts[2];
  if (!itemStringRaw.startsWith('H')) {
    return null;
  }

  const itemParts = itemStringRaw.slice(1).split(':');

  res.itemId = safeParseInt(itemParts[1]);
  res.enchantId = safeParseInt(itemParts[2]) || null;

  res.gemIds = [
    safeParseInt(itemParts[3]),
    safeParseInt(itemParts[4]),
    safeParseInt(itemParts[5]),
    safeParseInt(itemParts[6])
  ].filter(Boolean);

  const numBonus = safeParseInt(itemParts[13]);
  res.bonusIds = [];
  for (let i = 0; i < numBonus; i++) {
    const bonusId = safeParseInt(itemParts[14 + i]);
    if (bonusId) {
      res.bonusIds.push(bonusId);
    }
  }

  return res;
}

export function calcPageForOffset(offset: number, pageSize: number = 20) {
  return Math.ceil(offset / pageSize) - 1;
}

// like lodash's _.groupBy() but doesn't do field lookup and expects the lookup to be unique, so only use for simple groupBys
export function groupByDictUnique<TVAL>(list: TVAL[], field: string): { [key: string]: TVAL } {
  return list.reduce((accum, item) => {
    accum[(item as any)[field]] = item;
    return accum;
  }, {} as { [key: string]: TVAL });
}

// like lodash's _.groupBy() but does do field lookup and expects the lookup to be unique, so only use for simple groupBys
// will be slower than groupByDictUnique()
export function groupByDictUniqueWithFieldLookup<TVAL>(list: TVAL[], field: string): { [key: string]: TVAL } {
  return list.reduce((accum, item) => {
    accum[_.get(item, field)] = item;
    return accum;
  }, {} as { [key: string]: TVAL });
}

// like lodash's _.groupBy() but doesn't do field lookup, so only use for simple groupBys
export function groupByDictList<TVAL>(list: TVAL[], field: string): { [key: string]: TVAL[] } {
  return list.reduce((accum, item) => {
    const key = (item as any)[field];
    const val = accum[key];
    if (val) {
      val.push(item);
    } else {
      accum[key] = [item];
    }
    return accum;
  }, {} as { [key: string]: TVAL[] });
}

export function uniqBy<T>(array: T[], comparator?: (value: T) => string): T[] {
  if (!comparator) {
    return Array.from(new Set(array));
  }

  const matchLookup = new Set<string>();
  const uniqueArray: T[] = [];
  for (const item of array) {
    const computed = comparator(item);
    if (matchLookup.has(computed)) {
      continue;
    }

    matchLookup.add(computed);
    uniqueArray.push(item);
  }

  return uniqueArray;
}

// Alternative to Array.from specifically for sets
export function arrayFromSet<T>(set: Set<T>): T[] {
  const results: T[] = [];
  set.forEach((x: T) => results.push(x));
  return results;
}
